/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { type EmployeeBody, type RegisterData } from 'types';
import { type OrganizationInfo } from 'types/pages';
import ApiUtil from '../utils/ApiUtil';
import { userServiceApiUrls } from 'constants/apiEndPoints';
import QueryParamsBuilder from 'components/helpers/commonFunctions/QueryParamsBuilder';
import { strings } from 'i18n/Strings';

interface IEmployeeProps {
  empId: number;
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  employeeContribution?: string;
  employerContribution?: string;
  password: string | null;
  profileImage: string | null;
  rememberToken: string | null;
  emailVerifiedAt: string | null;
  employeeResetToken: string | null;
  isActive: boolean;
  createdBy: number;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
}


const register = async (data: RegisterData): Promise<unknown> =>
  await
    new Promise((resolve, reject) => {
      ApiUtil.postWithToken(`${String(userServiceApiUrls.registerUrl)}`, data)
        .then((employee) => {
          resolve(employee)
        }).catch((error) => { reject(error); })
    });

const registerOrgEmployee = async (data: RegisterData): Promise<unknown> =>
  await
    new Promise((resolve, reject) => {

      ApiUtil.postWithToken(`${userServiceApiUrls.registerOrgEmployeeUrl}/${data.organizationId}`, data)
        .then((employee) => {
          resolve(employee)
        }).catch((error) => { reject(error); })
    });

const uploadFile = async (employeeFile: File, organizationId: number, adminId: number): Promise<unknown> =>
  await
    new Promise((resolve, reject) => {

      const formData = new FormData();
      formData.append('file', employeeFile)
      ApiUtil.postWithToken(`${userServiceApiUrls.uploadFileUrl}/${organizationId}/${adminId}`, formData)
        .then((res) => {
          resolve(res)
        }).catch((error) => { reject(error); })

    })


const uploadFileDataValidation = async (data: unknown, organizationId: number, adminId: number): Promise<unknown> =>
  await
    new Promise((resolve, reject) => {
      ApiUtil.postWithToken(`${userServiceApiUrls.uploadFileDataValidationUrl}/${organizationId}/${adminId}`, { data })
        .then((res) => {
          resolve(res)
        }).catch((error) => { reject(error); })

    })

const uploadFileDataCreation = async (data: unknown, organizationId: number, adminId: number): Promise<unknown> =>
  await
    new Promise((resolve, reject) => {
      ApiUtil.postWithToken(`${userServiceApiUrls.uploadFileDataBulkCreateUrl}/${organizationId}/${adminId}`, { data })
        .then((res) => {
          resolve(res)
        }).catch((error) => { reject(error); })

    })

const uploadFileDataEmailCreation = async (data: unknown, organizationId: number, adminId: number): Promise<unknown> =>
  await
    new Promise((resolve, reject) => {
      ApiUtil.postWithToken(`${userServiceApiUrls.uploadFileDataBulkEmailCreateUrl}/${organizationId}/${adminId}`, { data })
        .then((res) => {
          resolve(res)
        }).catch((error) => { reject(error); })

    })

const getAllUsers = async ({ page, size, sortBy, sortOrder, searchValue }: {
  page: number;
  size: number;
  sortBy?: string;
  sortOrder?: string;
  searchValue?: string;
}): Promise<IEmployeeProps> => {
  const apiUrl = QueryParamsBuilder({ baseUrl: userServiceApiUrls.getAllUsersUrl, page, size, sortBy, sortOrder, searchValue });
  return await ApiUtil.getWithToken(apiUrl) as IEmployeeProps;

};


const getAllOrganizationAdmins = async ({ id, roleName, page, size, sortBy, sortOrder, searchValue }: {
  id: string | number;
  roleName: string;
  page: number;
  size: number;
  sortBy?: string;
  sortOrder?: string;
  searchValue?: string;
}): Promise<IEmployeeProps> => {

  const apiUrl = QueryParamsBuilder({ id, roleName, baseUrl: userServiceApiUrls.getAllOrganizationAdminsUrl, page, size, sortBy, sortOrder, searchValue });
  return await ApiUtil.getWithToken(apiUrl) as IEmployeeProps;

};
const getAllOrganizationEmployeeWithRoleName = async ({ id, page, size, sortBy, sortOrder, searchValue }: {
  id: string;
  page: number;
  size: number;
  sortBy?: string;
  sortOrder?: string;
  searchValue?: string;
}): Promise<IEmployeeProps> => {
  const apiUrl = QueryParamsBuilder({ id, baseUrl: userServiceApiUrls.getAllOrganizationEmployeeWithRoleNameUrl, page, size, sortBy, sortOrder, searchValue });
  return await ApiUtil.getWithToken(apiUrl) as IEmployeeProps;

};
const getAllOrganizationEmployeeDetails = async (id: string): Promise<OrganizationInfo> => {
  return await ApiUtil.getWithToken(`${userServiceApiUrls.getAllOrganizationEmployeeDetailsUrl}/${id}`) as OrganizationInfo;

};


const updateEmployee = async (id: number, body: EmployeeBody): Promise<unknown> => await
  new Promise((resolve, reject) => {

    ApiUtil.postWithToken(`${userServiceApiUrls.updateEmployeeUrl}/${id}`, body)
      .then((employee) => {
        resolve(employee);
      }).catch((error) => { reject(error); })
  });

const onboardNotifyEmployee = async (body: { email: string, errorType: string }): Promise<string | undefined> => {
  try {
    await ApiUtil.postWithToken(userServiceApiUrls.onboardNotifyEmployeeUrl, body);
    return strings.displayText.updateSuccess;
  } catch (error) {
    // handle error here if necessary
    throw new Error(`Unable to Update. ${String(error)}`);

  }
};

const removeEmployee = async (id: number): Promise<string> => {
  return await ApiUtil.postWithToken(`${userServiceApiUrls.removeEmployeeUrl}/${id}`) as string;
};

const removeAdminEmployee = async (id: number, comments?: string): Promise<string> => {
  try {
    await ApiUtil.postWithToken(`${userServiceApiUrls.removeAdminEmployeeUrl}/${id}`, { comments });
    return strings.displayText.deleteSuccess;
  } catch (error) {
    throw new Error(`Unable to delete employee. ${String(error)}`);
  }
};



const removeOrganizationEmployee = async (orgEmpId: number, comments: string, status?: string): Promise<unknown> =>
  await
    new Promise((resolve, reject) => {

      ApiUtil.postWithToken(`${userServiceApiUrls.removeOrganizationEmployeeUrl}/${orgEmpId}`, { comments, status })
        .then((res) => {
          resolve(res)
        }).catch((error) => { reject(error); })
    })

const reRegisterOrganizationEmployee = async (orgEmpId: number, comments: string, status?: string): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.postWithToken(`${userServiceApiUrls.reRegisterOrganizationEmployeeUrl}/${orgEmpId}`, { comments, status })
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });


const emailAlreadyExists = async (email: string): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.postWithoutToken(`${userServiceApiUrls.emailAlreadyExistsUrl}`, {
      email,
    }).then((res) => {
      resolve(res)
    }).catch((error) => { reject(error); })
  });



const getUserTokenById = async (empId: number): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.getWithoutToken(`${userServiceApiUrls.getUserTokenByIdUrl}/${empId}`)
      .then((res) => {
        resolve(res)
      }).catch((error) => { reject(error); })
  });


const getUserById = async (): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.getWithToken(`${userServiceApiUrls.getUserByIdUrl}`)
      .then((res) => {
        resolve(res)
      }).catch((error) => { reject(error); })
  });




const displayEmployeeInfo = async (id: string): Promise<unknown> =>
  await
    new Promise((resolve, reject) => {

      ApiUtil.getWithToken(`${userServiceApiUrls.displayEmployeeInfoUrl}/${id}`)
        .then((res) => {
          resolve(res)
        }).catch((error) => { reject(error); })
    });


const updateOrganizationEmployee = async (id: number, body: EmployeeBody): Promise<unknown> => await
  new Promise((resolve, reject) => {

    ApiUtil.postWithToken(`${userServiceApiUrls.updateOrganizationEmployeeUrl}/${id}`, body)
      .then((employee) => {
        resolve(employee)
      }).catch((error) => { reject(error); })
  });



const resendInviteLinkToOrgEmployee = async (orgEmpId: number): Promise<unknown> => await
  new Promise((resolve, reject) => {
    ApiUtil.getWithToken(`${userServiceApiUrls.resendInviteLinkToOrgEmployeeUrl}/${orgEmpId}`)
      .then((employee) => {
        resolve(employee)
      }).catch((error) => { reject(error); })
  });

const getEmployeeDetailsByUserId = async (userId: number): Promise<unknown> => await new Promise((resolve, reject) => {

  ApiUtil.getWithToken(`${userServiceApiUrls.getEmployeeDetailsByUserIdUrl}/${userId}`)
    .then((res) => {
      resolve(res)
    }).catch((error) => { reject(error); })
})

const getUserPension = async (userId: string): Promise<unknown> => await new Promise((resolve, reject) => {
  ApiUtil.getWithToken(`${userServiceApiUrls.getUserPensionUrl}/${userId}`)
    .then((res) => {
      resolve(res)
    }).catch((error) => { reject(error); })
})

const getUserPayments = async (userId: string): Promise<unknown> => await new Promise((resolve, reject) => {

  ApiUtil.getWithToken(`${userServiceApiUrls.getUserPaymentsUrl}/${userId}`)
    .then((res) => {
      resolve(res)
    }).catch((error) => { reject(error); })
})
const getUserTransactions = async (userId: string, type: string, page: number, size: number, searchValue: string): Promise<unknown> => await new Promise((resolve, reject) => {

  const url = `${userServiceApiUrls.getUserTransactionsUrl}/${userId}?page=${page}&size=${size}&searchValue=${searchValue}`
  ApiUtil.postWithToken(url, { type })
    .then((res) => {
      resolve(res)
    }).catch((error) => { reject(error); })
})
const getAllUserTransactions = async (userId: string, page: number, size: number, searchValue: string, orderType: string): Promise<unknown> => await new Promise((resolve, reject) => {

  const url = `${userServiceApiUrls.getAllUserTransactionsUrl}/${userId}?page=${page}&size=${size}&searchValue=${searchValue}&orderType=${orderType}`
  ApiUtil.postWithToken(url)
    .then((res) => {
      resolve(res)
    }).catch((error) => { reject(error); })
})

const getZpUserTransactions = async (page: number, size: number, sortBy: string,
  sortOrder: string, searchValue: string, filters: unknown): Promise<unknown> => await new Promise((resolve, reject) => {

    let url = QueryParamsBuilder({ baseUrl: userServiceApiUrls.getZpUserTransactionsUrl, page, size, sortBy, sortOrder, searchValue });

    const filterParams = new URLSearchParams();
    if (filters) {
      if (filters.startDate) {
        filterParams.append('startDate', `${filters.startDate} 00:00:00 +00:00`);
      }

      if (filters.endDate) {
        filterParams.append('endDate', `${filters.endDate} 23:59:59 +00:00`);
      }

      if (filters.oStartDate) {
        filterParams.append('oStartDate', `${filters.oStartDate} 00:00:00 +00:00`);
      }

      if (filters.oEndDate) {
        filterParams.append('oEndDate', `${filters.oEndDate} 23:59:59 +00:00`);
      }

      if (filters.sStartDate) {
        filterParams.append('sStartDate', `${filters.sStartDate} 00:00:00 +00:00`);
      }

      if (filters.sEndDate) {
        filterParams.append('sEndDate', `${filters.sEndDate} 00:00:00 +00:00`);
      }

      if (filters.fundType) {
        const fundType = filters.fundType || '';
        filterParams.append('fundType', fundType);
      }

      if (filters.status) {
        const status = filters.status || '';
        filterParams.append('status', status);
      }

      if (filters.orderType) {
        const orderType = filters.orderType || '';
        filterParams.append('orderType', orderType);
      }
    }
    if (filterParams.toString()) {
      url += `&${filterParams.toString()}`;
    }

    ApiUtil.postWithToken(url)
      .then((res) => {
        resolve(res)
      }).catch((error) => { reject(error); })
  })

const resendInviteLinkToOrgAdminEmployee = async (empId: number): Promise<unknown> => await
  new Promise((resolve, reject) => {

    ApiUtil.getWithToken(`${String(userServiceApiUrls.orgAdminEmployeeInvite)}${empId}`).then((employee) => {

      resolve(employee);
    })
      .catch((error) => { reject(error); })
  })

const fetchExportedEmployeeData = async (): Promise<unknown> =>
  await new Promise((resolve, reject) => {
    ApiUtil.getExcelFile('employees-export/')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        const url = URL.createObjectURL(blob);
        resolve(url);
      })
      .catch((error) => {
        reject(error);
      });
  });
const getAllRegisterOrgEmployee = async (orgId: number, startDate: string, frequency: string,): Promise<{ url: string; filename: string }> => await
  new Promise((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ApiUtil.getExcelFile(`${userServiceApiUrls.orgRegEmployee}/${orgId}?startDate=${startDate}&frequency=${frequency}`).then((res: any) => {
      const blob = new Blob([res.data], { type: res.headers['content-type'] });

      const url = URL.createObjectURL(blob);
      const contentDisposition = res.headers['content-disposition'];
      let filename = 'contribution.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*=UTF-8''(.+)|filename="(.+)"/);
        if (match) {
          filename = decodeURIComponent(match[1] || match[2]);
        }
      }

      resolve({ url, filename });
    })
      .catch((error) => { reject(error); })
  })




const searchOrganizationEmployee = async (searchValue: string, organizationId: number): Promise<unknown> => {
  try {
    return await ApiUtil.postWithToken(`org-employee-search/${organizationId}`, { searchValue })
  } catch (error) {
    throw new Error(`Unable to search employee. ${String(error)}`);
  }

}

const getUserPlan = async (userId: string): Promise<unknown> => {
  try {
    return await ApiUtil.getWithToken(`${userServiceApiUrls.getUserPlanUrl}/${userId}`)
  } catch (error) {
    throw new Error(`Unable to search employee. ${String(error)}`);
  }
}

export const getUserForReAllocationOfUnits = async (searchValue: string): Promise<unknown> => {
  try {
    const res = await ApiUtil.getWithToken(`${userServiceApiUrls.getUserByEmailOrUniqueId}/${searchValue}`);
    return res;
  } catch (error) {
    console.error('Error fetching details:', error);
    throw error;
  }
};

const getResource = async (key: string): Promise<unknown> => {
  try {
    if (!key) return null
    const data = await ApiUtil.getWithToken(`${userServiceApiUrls.getResource}?key=${key}`);
    return data;
  } catch (error) {
    throw new Error(`Unable to find the resource. ${String(error)}`);
  }
}


export {
  getResource,
  register,
  getAllUsers,
  getAllOrganizationAdmins,
  updateEmployee,
  removeEmployee,
  emailAlreadyExists,
  getUserTokenById,
  getUserById,
  getAllOrganizationEmployeeWithRoleName,
  uploadFile,
  getAllOrganizationEmployeeDetails,
  onboardNotifyEmployee,
  displayEmployeeInfo,
  registerOrgEmployee,
  updateOrganizationEmployee,
  removeOrganizationEmployee,
  resendInviteLinkToOrgEmployee,
  removeAdminEmployee,
  getEmployeeDetailsByUserId,
  getUserPension,
  getUserPayments,
  getUserTransactions,
  resendInviteLinkToOrgAdminEmployee,
  fetchExportedEmployeeData,
  getAllRegisterOrgEmployee,
  searchOrganizationEmployee,
  getUserPlan,
  reRegisterOrganizationEmployee,
  getZpUserTransactions,
  uploadFileDataValidation,
  uploadFileDataCreation,
  uploadFileDataEmailCreation,
  getAllUserTransactions
}